import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { type Theme } from '@mui/material/styles';
import { useCallback, useState, type MouseEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLogout, useMe, usePartnersReadMe } from '@/api/hooks';
import BrandLockup from '@/components/BrandLockup';
import { IconLogout } from '@/components/icon/tabler/IconLogout';
import PartnerNav from '@/components/template/PartnerNav';
import { showsPartnerNav } from '@/components/template/utils';
import { partnerStatusLabel } from '@/domain/account-status/utils';
import { useTranslate } from '@/state/i18n';
import { controlTokens, partnerTokens, surfaceTokens } from '@/theme/themePrimitives';
import { ScreenRoutes } from '@/types/router';
import { getUserInitials } from '@/utils/user';

interface Props {
  children: ReactNode;
}

/** Signed-in shell (design P05–P08, P17·b): top bar, section tabs and the profile menu. */
const PartnerTemplate = ({ children }: Props) => {
  const t = useTranslate('components.template.PartnerTemplate');
  const navigate = useNavigate();
  const { data: user } = useMe();
  const { data: account } = usePartnersReadMe();
  const partner = account?.partner;
  const { mutateAsync: logout } = useLogout();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const handleLogout = useCallback(async () => {
    setMenuAnchor(null);
    try {
      await logout();
    } finally {
      navigate(ScreenRoutes.SIGNIN, { replace: true });
    }
  }, [logout, navigate]);

  return (
    <Box sx={sx.page}>
      <Box component="header" sx={sx.bar}>
        <BrandLockup descriptor={t('brandTag')} size="bar" />
        {partner ? (
          <Box sx={sx.account}>
            <Avatar src={partner.logoUrl ?? undefined} variant="rounded" sx={sx.accountLogo}>
              {partner.displayName.slice(0, 2).toUpperCase()}
            </Avatar>
            <Box sx={sx.accountText}>
              <Typography variant="subtitle2" noWrap>
                {partner.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {partnerStatusLabel[partner.status]}
              </Typography>
            </Box>
          </Box>
        ) : null}
        <Box sx={sx.profile}>
          {user ? (
            <Typography variant="body2" sx={sx.userName}>
              {user.firstName}
            </Typography>
          ) : null}
          <IconButton
            aria-label={t('profileMenu')}
            aria-haspopup="menu"
            onClick={(event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget)}
          >
            <Avatar sx={sx.avatar}>{getUserInitials(user)}</Avatar>
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <IconLogout fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('logout')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      {showsPartnerNav(account) ? <PartnerNav /> : null}
      <Box component="main" sx={sx.main}>
        {children}
      </Box>
    </Box>
  );
};

export default PartnerTemplate;

const sx = {
  page: { minHeight: '100dvh', bgcolor: 'background.default' },
  bar: (theme: Theme) => ({
    height: 64,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    px: { xs: 2, md: 3 },
    bgcolor: partnerTokens.bar,
    ...theme.applyStyles('dark', {
      bgcolor: 'background.paper',
      borderBottom: '1px solid',
      borderColor: 'divider',
    }),
  }),
  account: (theme: Theme) => ({
    display: { xs: 'none', sm: 'flex' },
    alignItems: 'center',
    gap: 1.25,
    height: 44,
    pl: 0.75,
    pr: 1.5,
    ml: 1,
    borderRadius: '14px',
    border: 1,
    borderColor: partnerTokens.border,
    bgcolor: surfaceTokens.paper,
    minWidth: 0,
    ...theme.applyStyles('dark', { bgcolor: 'background.paper', borderColor: 'divider' }),
  }),
  accountLogo: {
    width: 32,
    height: 32,
    borderRadius: '10px',
    fontSize: 12,
    fontWeight: 700,
    bgcolor: controlTokens.primary,
    color: 'common.white',
  },
  accountText: { display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.1 },
  profile: { ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 },
  userName: (theme: Theme) => ({
    display: { xs: 'none', sm: 'block' },
    color: partnerTokens.ink,
    ...theme.applyStyles('dark', { color: 'text.secondary' }),
  }),
  avatar: {
    width: 32,
    height: 32,
    fontSize: 12,
    fontWeight: 600,
    bgcolor: partnerTokens.softBlue,
    color: controlTokens.primary,
  },
  main: {
    width: '100%',
    maxWidth: 1280,
    mx: 'auto',
    px: { xs: 2, md: 4 },
    py: { xs: 3, md: 4 },
  },
};
